import { Plus, Undo2 } from "lucide-react";
import { useId, useState } from "react";

import {
  type SubstitutionBucketName,
  type SubstitutionBucketView,
  type SubstitutionEditorRow,
  type SubstitutionGenderForm,
} from "@/lib/rulesContent";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { Input, Label } from "@/ui/Input";
import { Tooltip } from "@/ui/Tooltip";

/**
 * The named substitution tables (#910).
 *
 * WHAT THIS REPLACES. A Rule's `substitutions` document reached the clinician as
 * a JSON textarea under a free-text "Part key" input, or — after the named
 * editors landed — as "this document is not authored in this editor" and nothing
 * else. Neither can express what MOD and NII actually change: four named tables
 * of literal find/replace text, one applied to every patient and three selected
 * by the recorded gender form. So the document gets named controls, and the part
 * key never appears.
 *
 * WHY THERE ARE FOUR TABLES AND NOT ONE. The names are the pinned provider's:
 * `_get_substitution_bucket` reads `common` and folds ONE gender bucket over it,
 * `{**common, **bucket}`. A single flat table would have to invent a per-row
 * gender column and then decide what two rows with the same find text mean —
 * which is a merge rule the engine already owns and states differently.
 *
 * INHERITED ROWS ARE ROWS. An authored Rule's document is an OVERLAY on base
 * canon's, so the runtime applies base's entries plus this document's. Showing
 * only the authored ones would tell a clinician the runtime has three
 * substitutions when it has four hundred, and would make removing a row read as
 * a deletion when the engine's answer is inheritance. Every row therefore says
 * where its replacement comes from, and the SERVICE composed that — this
 * component renders the answer and never derives it.
 *
 * AN EMPTY REPLACEMENT IS A VALUE. The source table carries a phrase whose
 * replacement is the empty string, which DELETES it from the reply. So an empty
 * field is a real, savable state, it is labelled as one rather than left looking
 * unfinished, and nothing here treats it as absence.
 *
 * FIND TEXT IS IDENTITY AND IS NOT EDITED IN PLACE. An entry IS its find text —
 * it is the key of the map the provider compiles — so changing it is removing
 * one entry and adding another, exactly as `PartsEditor` says of a part's kind.
 * An in-place rename would additionally have no honest answer for a base row: the
 * base find text would still match at runtime, and the "rename" would silently
 * become an unrelated addition. So the field is read-only and "Add substitution"
 * is where a find text is entered, once, against a check for a duplicate.
 */

const ORIGIN_LABELS: Record<SubstitutionEditorRow["origin"], string> = {
  base: "Base library",
  authored: "Changed here",
  inherited: "Inherited",
};

/** What an empty replacement is, in words, wherever one has to be described. */
const EMPTY_REPLACEMENT = "removed from the reply";

/**
 * How many rows of one table render before the clinician asks for the rest.
 *
 * The shared Hebrew table in the real base pack carries 205 entries and a
 * MOD-equivalent rule changes four of them, so the default view is a working
 * one rather than a wall. The COUNT above the table always states the whole,
 * the filter reaches any row, and "Show all" renders the rest — nothing is
 * unreachable and no number on screen is the capped one.
 */
const VISIBLE_ROW_LIMIT = 25;

function RowFields({
  row,
  readOnly,
  composed,
  onReplacement,
  onReset,
}: {
  row: SubstitutionEditorRow;
  readOnly: boolean;
  /**
   * Whether the SERVICE has said what this rule inherits.
   *
   * LOAD-BEARING FOR THE RESET LABEL, not decoration. Until the composition
   * lands, every row the draft carries looks like an addition — there is no base
   * map to compare it against — so a label reading "the base library does not
   * have it" would be a claim about base made before base was read. The ACTION
   * is the same either way (remove the authored entry), so the control stays;
   * what changes is that it stops asserting the consequence it cannot know.
   */
  composed: boolean;
  onReplacement: (replacement: string) => void;
  onReset: () => void;
}) {
  const findId = useId();
  const replacementId = useId();
  const authored = row.origin === "authored";

  return (
    <li className="grid gap-2 border-b border-border px-3 py-2 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
      <div className="space-y-1">
        <Label htmlFor={findId} className="text-xs">
          Find
        </Label>
        {/* `dir="auto"` rather than a fixed direction: this asset is bilingual
            and one table can hold Hebrew and Latin text at once, so the browser
            reads each value's own first strong character. A hard-coded `rtl`
            would mis-render the English rows of the same document. */}
        <Input
          id={findId}
          dir="auto"
          // Read-only AND visibly so: a field that looks like every editable one
          // beside it invites an edit the control will not take, which reads as
          // a broken input rather than as a deliberate limit.
          className="bg-muted text-muted-foreground"
          value={row.find}
          readOnly
          aria-disabled="true"
          onChange={() => undefined}
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor={replacementId} className="text-xs">
          Replace with
        </Label>
        <Input
          id={replacementId}
          dir="auto"
          value={row.replacement}
          readOnly={readOnly}
          aria-disabled={readOnly || undefined}
          // The empty state is a real replacement, so the field says what an
          // empty field MEANS rather than asking to be filled in.
          placeholder={EMPTY_REPLACEMENT[0]?.toUpperCase() + EMPTY_REPLACEMENT.slice(1)}
          onChange={(event) => onReplacement(event.target.value)}
        />
      </div>

      <div className="flex items-center gap-2 sm:pb-1">
        <Badge tone="outline">{ORIGIN_LABELS[row.origin]}</Badge>
        {readOnly || !authored ? null : (
          <Tooltip
            content={
              !composed
                ? "Remove this change from the rule. What the base library holds for it has not been read yet."
                : row.inheritedReplacement === null
                  ? "Remove this substitution. The base library does not have it, so nothing replaces it."
                  : `Stop changing this here and use the base library's replacement (${row.inheritedReplacement === "" ? EMPTY_REPLACEMENT : row.inheritedReplacement}).`
            }
          >
            <IconButton
              label={
                !composed
                  ? `Remove the change to ${row.find}`
                  : row.inheritedReplacement === null
                    ? `Remove the substitution for ${row.find}`
                    : `Restore the inherited replacement for ${row.find}`
              }
              icon={<Undo2 className="h-4 w-4" />}
              onClick={onReset}
            />
          </Tooltip>
        )}
      </div>
    </li>
  );
}

function BucketTable({
  view,
  genderForm,
  readOnly,
  composed,
  onSet,
}: {
  view: SubstitutionBucketView;
  /**
   * What the SERVICE says the runtime applies for this bucket's gender form,
   * or `null` for `common` and wherever the projection did not answer.
   */
  genderForm: SubstitutionGenderForm | null;
  readOnly: boolean;
  composed: boolean;
  onSet: (find: string, replacement: string | null) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draftFind, setDraftFind] = useState("");
  const [filter, setFilter] = useState("");
  const [changedOnly, setChangedOnly] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const headingId = useId();
  const addFindId = useId();
  const addProblemId = useId();
  const filterId = useId();
  const changedOnlyId = useId();

  const finds = new Set(view.rows.map((row) => row.find));
  const authoredCount = view.rows.filter(
    (row) => row.origin === "authored",
  ).length;
  const addRefusal =
    draftFind.length === 0
      ? "Enter the text to find first."
      : finds.has(draftFind)
        ? "This table already has a substitution for that text."
        : null;

  // THE SHARED TABLE IS TWO HUNDRED ROWS IN THE REAL PACK, and a MOD-equivalent
  // rule changes four of them. So the table has to answer two questions rather
  // than one: "what does the runtime apply" — which is every row, and why none
  // is hidden by default — and "what did I change here", which is the toggle.
  // Both filters narrow what is RENDERED and neither changes what is stored, so
  // a filtered table is never a smaller document.
  const needle = filter.trim().toLocaleLowerCase();
  const matching = view.rows.filter(
    (row) =>
      (!changedOnly || row.origin === "authored") &&
      (needle === "" ||
        row.find.toLocaleLowerCase().includes(needle) ||
        row.replacement.toLocaleLowerCase().includes(needle)),
  );
  // A cap rather than virtualisation: it costs one sentence and one button, it
  // degrades to plain HTML, and the count above it always states the whole. The
  // alternative renders four hundred pairs of inputs into the accessibility tree
  // for a clinician who is looking for one of them.
  const visible = showAll ? matching : matching.slice(0, VISIBLE_ROW_LIMIT);
  const hidden = matching.length - visible.length;

  return (
    <section aria-labelledby={headingId} className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id={headingId} className="text-sm font-medium text-foreground">
          {view.label}
        </h3>
        <p className="text-xs text-muted-foreground">
          {view.rows.length} {view.rows.length === 1 ? "entry" : "entries"}
          {authoredCount > 0 ? ` · ${authoredCount} changed here` : null}
          {/* The runtime's own number, from the service. It is NOT this table's
              length: a gender bucket is applied ON TOP of the shared one, so
              what the runtime holds for a female patient is the union — and a
              clinician reading a four-row table as "four substitutions apply"
              would be wrong by four hundred. */}
          {genderForm
            ? ` · ${genderForm.entryCount} applied at runtime for this form`
            : null}
        </p>
      </div>
      <p className="text-xs text-muted-foreground">{view.hint}</p>
      {genderForm && genderForm.overridesCommon.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          {genderForm.overridesCommon.length}{" "}
          {genderForm.overridesCommon.length === 1 ? "entry" : "entries"} here
          replace the shared table&apos;s.
        </p>
      ) : null}

      {view.rows.length === 0 ? (
        <p className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
          No substitutions in this table.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-0 flex-1 space-y-1">
              <Label htmlFor={filterId} className="text-xs">
                Find in {view.label.toLocaleLowerCase()}
              </Label>
              <Input
                id={filterId}
                dir="auto"
                type="search"
                value={filter}
                placeholder="Filter by text or replacement"
                onChange={(event) => setFilter(event.target.value)}
              />
            </div>
            <label
              htmlFor={changedOnlyId}
              className="flex items-center gap-2 pb-3 text-xs text-muted-foreground"
            >
              <input
                id={changedOnlyId}
                type="checkbox"
                checked={changedOnly}
                onChange={(event) => setChangedOnly(event.target.checked)}
              />
              Changed here only
            </label>
          </div>

          {matching.length === 0 ? (
            <p className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
              No entry in this table matches.
            </p>
          ) : (
            <ul
              aria-label={`${view.label} substitutions`}
              className="overflow-hidden rounded-md border border-border bg-card"
            >
              {visible.map((row) => (
                <RowFields
                  key={row.find}
                  row={row}
                  readOnly={readOnly}
                  composed={composed}
                  onReplacement={(replacement) => onSet(row.find, replacement)}
                  onReset={() => onSet(row.find, null)}
                />
              ))}
            </ul>
          )}

          {hidden > 0 ? (
            <p className="text-xs text-muted-foreground">
              Showing {visible.length} of {matching.length}.{" "}
              <button
                type="button"
                className="underline hover:text-foreground"
                onClick={() => setShowAll(true)}
              >
                Show all {matching.length} entries
              </button>
            </p>
          ) : null}
        </>
      )}

      {readOnly ? null : adding ? (
        <div className="space-y-1">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1 space-y-1">
              <Label htmlFor={addFindId} className="text-xs">
                Text to find
              </Label>
              <Input
                id={addFindId}
                dir="auto"
                value={draftFind}
                aria-invalid={finds.has(draftFind) || undefined}
                aria-describedby={finds.has(draftFind) ? addProblemId : undefined}
                onChange={(event) => setDraftFind(event.target.value)}
              />
            </div>
            <Tooltip content={addRefusal ?? "Add this substitution"}>
              <Button
                size="sm"
                disabled={addRefusal !== null}
                onClick={() => {
                  // Added with an EMPTY replacement, which is a real and
                  // complete state — the entry deletes the found text — rather
                  // than a placeholder the clinician has to clear.
                  onSet(draftFind, "");
                  setDraftFind("");
                  setAdding(false);
                }}
              >
                Add
              </Button>
            </Tooltip>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDraftFind("");
                setAdding(false);
              }}
            >
              Cancel
            </Button>
          </div>
          {finds.has(draftFind) ? (
            <div role="alert">
              <p id={addProblemId} className="text-xs text-destructive">
                This table already has a substitution for that text. Change its
                replacement instead — two entries cannot find the same words.
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
          <Plus aria-hidden="true" className="h-4 w-4" />
          Add substitution
        </Button>
      )}
    </section>
  );
}

/**
 * Every bucket of one language, in the provider's own order.
 *
 * Presentational and total, on `PartsEditor`'s rule: it owns no draft and
 * performs no request, so the same component renders the authoring surface and
 * the read-only one and `readOnly` is a single prop every control reads.
 */
export function SubstitutionTables({
  buckets,
  genderForms,
  readOnly,
  composed,
  onSet,
}: {
  buckets: readonly SubstitutionBucketView[];
  genderForms: readonly SubstitutionGenderForm[];
  readOnly: boolean;
  /** Whether the service has said what this rule inherits — see `RowFields`. */
  composed: boolean;
  onSet: (
    bucket: SubstitutionBucketName,
    find: string,
    replacement: string | null,
  ) => void;
}) {
  return (
    <div className="space-y-6">
      {buckets.map((view) => (
        <BucketTable
          key={view.bucket}
          view={view}
          genderForm={
            genderForms.find((form) => form.genderform === view.bucket) ?? null
          }
          readOnly={readOnly}
          composed={composed}
          onSet={(find, replacement) => onSet(view.bucket, find, replacement)}
        />
      ))}
    </div>
  );
}
